import type {
  DynamicRegenSync,
  RiskAssessmentGoalOutcome,
  RiskAssessmentInput,
  RiskAssessmentSubmitInput,
  SnapshotInvalidation,
  SubmitInstanceRiskAssessmentResponse,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, apiErrorCode } from "@/lib/api/_client";
import * as riskAssessmentApi from "@/lib/api/flow-instances/risk-assessment";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { flowInstanceQueryKeys } from "./use-flow-instances";
import { goalSnapshotQueryKeys } from "./use-goal-snapshots";
import { invalidateSuitabilityQueries } from "./use-goals";
import { useInvalidatePortfolioSnapshots } from "./use-portfolio-snapshots";
import { useInvalidateDynamicSync } from "./use-portfolios";

const riskAssessmentKeys = {
  flowInstanceValue: (instanceId: string | null, goalId: string | null) =>
    ["flowInstances", instanceId, "risk-assessment", goalId, "flow-instance-value"] as const,
};

/**
 * QP-180 — read the persisted risk-assessment component value for a goal
 * (inputData carries the client's `adjustmentAnswers`, which the goal columns
 * don't). Used to seed the widget's adjustment-question answers on reload.
 *
 * QL-212 — the service returns this goal's EFFECTIVE inputs: an input the risk
 * model captures once for the client comes back here as well, so nothing on the
 * goal page has to know where the answer was stored.
 */
export function useRiskAssessmentValue(instanceId: string | null, goalId: string | null) {
  const { data } = useQuery({
    queryKey: riskAssessmentKeys.flowInstanceValue(instanceId, goalId),
    queryFn: () => riskAssessmentApi.getFlowInstanceValue(instanceId!, goalId!),
    enabled: !!instanceId && !!goalId,
  });
  return { inputData: data?.inputData ?? null, calculatedData: data?.calculatedData ?? null };
}

export function useSubmitRiskAssessment(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidatePortfolioSnapshots = useInvalidatePortfolioSnapshots();
  const invalidateDynamicSync = useInvalidateDynamicSync();
  return useMutation({
    mutationFn: ({
      goalId,
      data,
      isComplete,
    }: {
      goalId: string;
      data: RiskAssessmentSubmitInput;
      isComplete: boolean;
    }) =>
      isComplete
        ? riskAssessmentApi.submitRiskAssessment(instanceId, goalId, data)
        : riskAssessmentApi.savePartial(instanceId, goalId, data),
    onSuccess: (result, { goalId, isComplete }) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(riskAssessmentKeys.flowInstanceValue(instanceId, goalId), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
      }
      if (isComplete) {
        queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.goals(instanceId) });
        queryClient.invalidateQueries({
          queryKey: goalSnapshotQueryKeys.instanceCost(instanceId),
        });
        queryClient.invalidateQueries({
          queryKey: goalSnapshotQueryKeys.instanceExpectedValue(instanceId),
        });
        // Invalidate all goal snapshot queries so visualization components refresh
        queryClient.invalidateQueries({
          queryKey: goalSnapshotQueryKeys.allForGoal(goalId),
        });
        // QP-150 — snapshots are portfolio-scoped. The API returns refreshHints keyed by
        // portfolioId; invalidate those portfolio snapshot queries so widgets (xray, forecast,
        // cost table, etc.) on the portfolio page pick up the regenerated data.
        const hints = (result as { refreshHints?: SnapshotInvalidation[] }).refreshHints;
        if (hints) {
          for (const hint of hints) {
            invalidatePortfolioSnapshots(hint.portfolioId, hint);
          }
        }
        // QP-185 — the new score regenerated the goal's dynamic
        // portfolios server-side; refresh their composition queries.
        invalidateDynamicSync((result as { dynamicSync?: DynamicRegenSync }).dynamicSync);
        // Slice 3 — a successful submission flips goal.hasRiskAssessment server-side, which
        // is the engine's completion signal for risk_assessment. Partial saves don't change
        // it, so we only invalidate completion on the isComplete branch.
        invalidateCompletion(instanceId);
        // QL-10 — the goal's risk level and risk score are suitability inputs,
        // so the builder's verdicts go stale on submit. Only `submit` writes them to the
        // goal (partial saves don't), hence the isComplete branch.
        invalidateSuitabilityQueries(queryClient);
      }
    },
  });
}

// ─── Instance-level (client-level) capture — QL-212 ──────────────────────────

/**
 * The instance-level answer's cache key.
 *
 * The literal `"instance"` segment matters: the goal key above puts a possibly
 * `null` goalId in that same position, so a bare `null` here would collide with
 * a disabled per-goal query.
 */
const instanceRiskAssessmentKeys = {
  flowInstanceValue: (instanceId: string | null) =>
    ["flowInstances", instanceId, "risk-assessment", "instance", "flow-instance-value"] as const,
};

/**
 * QL-212 — read the client-level risk answer (the `goalId IS NULL` row). Seeds
 * the widget on a global page, the way `useRiskAssessmentValue` seeds it on a
 * goal page.
 */
export function useInstanceRiskAssessmentValue(
  instanceId: string | null,
  riskModelId: string | null,
) {
  const { data } = useQuery({
    queryKey: instanceRiskAssessmentKeys.flowInstanceValue(instanceId),
    queryFn: () => riskAssessmentApi.getInstanceValue(instanceId!),
    // A flow with no risk model has no assessment to read; the service does not
    // check, so gate here rather than firing a pointless request.
    enabled: !!instanceId && !!riskModelId,
  });
  return { inputData: data?.inputData ?? null, calculatedData: data?.calculatedData ?? null };
}

/**
 * QL-212 — save or submit the client-level answer.
 *
 * Same autosave contract as the per-goal hook (partial while incomplete, submit
 * once complete, no submit button); what differs is the blast radius. A global
 * submit scores EVERY goal, so the invalidation fans out over the goals the
 * server says it touched rather than the single goal the caller named.
 */
export function useSubmitInstanceRiskAssessment(
  instanceId: string,
  /** Translated toast copy — the hook has no language of its own. */
  messages: { saveFailed: string; matrixGap: string },
) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidatePortfolioSnapshots = useInvalidatePortfolioSnapshots();
  const invalidateDynamicSync = useInvalidateDynamicSync();

  return useMutation({
    mutationFn: ({ data, isComplete }: { data: RiskAssessmentInput; isComplete: boolean }) =>
      isComplete
        ? riskAssessmentApi.submitInstanceRiskAssessment(instanceId, data)
        : riskAssessmentApi.saveInstancePartial(instanceId, data),
    onError: (err) => {
      // The widget autosaves and has no error surface of its own, so a refused
      // write (e.g. `risk_matrix_gap` — the model has no cell for the chosen
      // pair, nothing stored) would otherwise vanish. The matrix gap — the one
      // expected refusal — gets translated copy; other API errors keep the
      // server's message, transport errors a translated generic one. A fixed id
      // keeps repeated autosave failures from stacking toasts.
      const message =
        apiErrorCode(err) === "risk_matrix_gap"
          ? messages.matrixGap
          : err instanceof ApiError
            ? err.message
            : messages.saveFailed;
      toast.error(message, { id: "instance-risk-assessment-save" });
    },
    onSuccess: (result, { isComplete }) => {
      if (!result.success) return;
      if (result.inputData !== undefined) {
        queryClient.setQueryData(instanceRiskAssessmentKeys.flowInstanceValue(instanceId), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
      }
      // QL-212 — every goal-keyed read is the MERGED one, so any stored
      // client-level answer (partial saves included) changes what each goal's
      // page shows. A partial save names no goals, so match the key shape
      // rather than the fan-out's outcomes; skip the instance row seeded above.
      queryClient.invalidateQueries({
        predicate: ({ queryKey: k }) =>
          k[0] === "flowInstances" &&
          k[1] === instanceId &&
          k[2] === "risk-assessment" &&
          k[3] !== "instance" &&
          k[4] === "flow-instance-value",
      });
      if (!isComplete) return;

      queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.goals(instanceId) });
      queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.instanceCost(instanceId) });
      queryClient.invalidateQueries({
        queryKey: goalSnapshotQueryKeys.instanceExpectedValue(instanceId),
      });

      // The fan-out's per-goal report is also the list of goals whose snapshots
      // may have moved — every goal the answer was applied to, and every goal it
      // failed, since a failed goal that had a score is reset to unscored.
      const outcomes =
        (result as SubmitInstanceRiskAssessmentResponse).goalOutcomes ??
        ([] as RiskAssessmentGoalOutcome[]);
      for (const outcome of outcomes) {
        if (outcome.status === "pending") continue;
        queryClient.invalidateQueries({
          queryKey: goalSnapshotQueryKeys.allForGoal(outcome.goalId),
        });
      }

      // QP-150 — portfolio-scoped snapshot queries, named by the server. Under
      // QL-212's D9 gate these name only what actually changed, so a re-submit
      // of the same answers invalidates nothing.
      const hints = (result as SubmitInstanceRiskAssessmentResponse).refreshHints;
      if (hints) {
        for (const hint of hints) invalidatePortfolioSnapshots(hint.portfolioId, hint);
      }
      invalidateDynamicSync((result as SubmitInstanceRiskAssessmentResponse).dynamicSync);

      invalidateCompletion(instanceId);
      // QL-10 — every goal's risk level and score are suitability inputs, and a
      // global submit moves all of them at once.
      invalidateSuitabilityQueries(queryClient);
    },
  });
}
